import React, { useMemo, useState } from 'react';
import { X, Star, Search } from 'lucide-react';
import type { Product, Category } from '../types/order';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Τα διαθέσιμα προϊόντα του ταμείου (ίδια λίστα με τον κατάλογο). */
  products: Product[];
  categories: Category[];
  /** Τα αγαπημένα με τη σειρά τους. */
  favoriteIds: number[];
  onToggle: (productId: number) => void;
}

// «ΚΟΥΛΟΎΡΙ» και «κουλουρι» πρέπει να βρίσκουν το ίδιο προϊόν.
const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * «Αγαπημένα» του take away (μενού χρήστη): ο ταμίας σημειώνει με ⭐ τα προϊόντα
 * που φεύγουν συχνά. Ισχύουν για ΟΛΟ το κατάστημα και εμφανίζονται πρώτα στον
 * κατάλογο ως κατηγορία «Αγαπημένα». Κάθε πάτημα αποθηκεύεται αμέσως.
 */
const TakeAwayFavoritesModal: React.FC<Props> = ({ isOpen, onClose, products, categories, favoriteIds, onToggle }) => {
  const [search, setSearch] = useState('');

  // Ομάδες ανά κατηγορία, με τη σειρά του καταλόγου. Κρυφές κατηγορίες (και τα
  // προϊόντα τους) δεν εμφανίζονται, όπως και στον κατάλογο του ταμείου.
  const groups = useMemo(() => {
    const label = new Map<number, string>();
    const order: number[] = [];
    const hidden = new Set<number>();
    categories.forEach(c => {
      if (c.is_hidden) hidden.add(c.id);
      label.set(c.id, c.is_system ? 'Χωρίς κατηγορία' : c.name);
      order.push(c.id);
      (c.subcategories || []).forEach(s => {
        if (c.is_hidden || s.is_hidden) hidden.add(s.id);
        label.set(s.id, `${c.name} › ${s.name}`);
        order.push(s.id);
      });
    });
    const byCategory = new Map<number, Product[]>();
    products.forEach(p => {
      if (hidden.has(p.category_id)) return;
      const list = byCategory.get(p.category_id) || [];
      list.push(p);
      byCategory.set(p.category_id, list);
    });
    const known = order.filter(id => byCategory.has(id));
    const unknown = Array.from(byCategory.keys()).filter(id => !label.has(id));
    return [...known, ...unknown].map(id => ({
      id,
      name: label.get(id) || 'Χωρίς κατηγορία',
      products: byCategory.get(id) || [],
    }));
  }, [products, categories]);

  const visibleById = useMemo(() => {
    const m = new Map<number, Product>();
    groups.forEach(g => g.products.forEach(p => m.set(p.id, p)));
    return m;
  }, [groups]);

  if (!isOpen) return null;

  const favoriteSet = new Set(favoriteIds);
  const selected = favoriteIds.map(id => visibleById.get(id)).filter((p): p is Product => !!p);
  const needle = fold(search.trim());
  const shownGroups = needle
    ? groups
        .map(g => ({ ...g, products: g.products.filter(p => fold(p.name).includes(needle)) }))
        .filter(g => g.products.length > 0)
    : groups;

  return (
    <div
      className="cashier-overlay fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      {/* Σταθερό ύψος: με την αναζήτηση η λίστα μικραίνει και το παράθυρο δεν πρέπει να «πηδάει». */}
      <div
        className="cashier-dialog bg-surface-0 rounded-2xl w-full max-w-4xl h-[85vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Αγαπημένα"
      >
        <div className="cashier-dialog-header flex items-center justify-between gap-3 px-5 py-4 border-b border-line bg-surface-1 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
              <Star className="h-5 w-5 text-amber-600 dark:text-amber-400 fill-current" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-t1">Αγαπημένα</h2>
              <p className="text-xs text-t3 mt-0.5">
                Πάτα ⭐ στα προϊόντα που φεύγουν συχνά — εμφανίζονται πρώτα στον κατάλογο, σε όλα τα ταμεία του καταστήματος.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center active:scale-95 transition-all shrink-0"
            title="Κλείσιμο"
          >
            <X className="h-5 w-5 text-t2" />
          </button>
        </div>

        {/* Τα επιλεγμένα, με τη σειρά που εμφανίζονται στον κατάλογο */}
        <div className="px-5 py-3 border-b border-line bg-surface-1/60 shrink-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-t3 mb-2">
            Στα αγαπημένα ({selected.length})
          </div>
          {selected.length === 0 ? (
            <p className="text-sm text-t3">
              Κανένα ακόμα. Μόλις σημειώσεις ένα, εμφανίζεται η κατηγορία «Αγαπημένα» στον κατάλογο.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
              {selected.map(p => (
                <button
                  key={p.id}
                  onClick={() => onToggle(p.id)}
                  title="Αφαίρεση από τα αγαπημένα"
                  className="flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700/60 hover:bg-amber-200 dark:hover:bg-amber-900/60 active:scale-95 transition-all touch-manipulation"
                >
                  <Star className="h-3.5 w-3.5 fill-current" />
                  {p.name}
                  <X className="h-3.5 w-3.5 opacity-60" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 pt-3 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-t3" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Αναζήτηση προϊόντος…"
              className="w-full h-11 pl-10 pr-10 rounded-xl bg-surface-2 border border-line-strong text-t1 placeholder:text-t3 focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400 text-sm"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-t3 hover:text-t1"
                title="Καθαρισμός"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-y-auto px-5 py-3 space-y-4 flex-1">
          {shownGroups.length === 0 && (
            <p className="text-center text-sm text-t3 py-8">Δεν βρέθηκε προϊόν.</p>
          )}
          {shownGroups.map(g => (
            <div key={g.id}>
              <div className="text-xs font-semibold uppercase tracking-wide text-t3 mb-2">{g.name}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {g.products.map(p => {
                  const on = favoriteSet.has(p.id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => onToggle(p.id)}
                      aria-pressed={on}
                      className={`flex items-center gap-2.5 px-3 py-3 rounded-xl border text-left transition-colors touch-manipulation active:scale-[0.98] ${on
                        ? 'border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/30'
                        : 'border-line bg-surface-1 hover:bg-surface-3/60'}`}
                    >
                      <Star className={`h-5 w-5 shrink-0 ${on ? 'text-amber-500 fill-current' : 'text-t4'}`} />
                      <span className={`text-sm leading-tight line-clamp-2 ${on ? 'font-semibold text-t1' : 'text-t2'}`}>{p.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-line bg-surface-1 shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl bg-surface-3 hover:bg-surface-2 text-sm font-semibold text-t1 transition-colors"
          >
            Κλείσιμο
          </button>
        </div>
      </div>
    </div>
  );
};

export default TakeAwayFavoritesModal;
